import React from 'react';
import { ContentType, SubType } from '../../../../api/types';

interface FilterState {
  activeContentTypes: Set<ContentType>;
  activeSubTypes: Set<SubType>;
}

interface FilterBlockProps {
  filterState: FilterState;
  updateContentTypes: (types: Set<ContentType>) => void;
  updateSubTypes: (types: Set<SubType>) => void;
}

// Content-type → subtype map, lifted from the old ProfilePanel.
const contentTypeSubTypes = {
  [ContentType.TEXT]: [SubType.ARTICLE, SubType.NEWSLETTER, SubType.SPARK],
  [ContentType.AUDIO]: [SubType.PODCAST, SubType.SONG, SubType.MEMO],
  [ContentType.VIDEO]: [SubType.LONG, SubType.SHORT, SubType.LIVE],
  [ContentType.IMAGE]: [SubType.SINGLE, SubType.GALLERY],
};

const TYPE_NAMES: Record<ContentType, string> = {
  [ContentType.TEXT]: 'Text',
  [ContentType.AUDIO]: 'Audio',
  [ContentType.VIDEO]: 'Video',
  [ContentType.IMAGE]: 'Image',
};

const SUBTYPE_NAMES: Partial<Record<SubType, string>> = {
  [SubType.ARTICLE]: 'Articles',
  [SubType.NEWSLETTER]: 'Newsletters',
  [SubType.SPARK]: 'Sparks',
  [SubType.NEWS]: 'News',
  [SubType.ANALYSIS]: 'Analysis',
  [SubType.OPINION]: 'Opinion',
  [SubType.EDUCATIONAL]: 'Educational',
  [SubType.ENTERTAINMENT]: 'Entertainment',
  [SubType.SATIRE]: 'Satire',
  [SubType.ADVERTORIAL]: 'Advertorial',
  [SubType.PODCAST]: 'Podcasts',
  [SubType.SONG]: 'Songs',
  [SubType.MEMO]: 'Memos',
  [SubType.LONG]: 'Long Videos',
  [SubType.SHORT]: 'Short Videos',
  [SubType.LIVE]: 'Live Videos',
  [SubType.SINGLE]: 'Single Images',
  [SubType.GALLERY]: 'Galleries',
};

/** Content preference chips — same toggle logic as the old panel, glass chips. */
const FilterBlock: React.FC<FilterBlockProps> = ({
  filterState,
  updateContentTypes,
  updateSubTypes,
}) => {
  const { activeContentTypes, activeSubTypes } = filterState;

  const handleContentTypeToggle = (contentType: ContentType) => {
    const newActiveContentTypes = new Set(activeContentTypes);
    const newActiveSubTypes = new Set(activeSubTypes);

    if (activeContentTypes.has(contentType)) {
      newActiveContentTypes.delete(contentType);
      contentTypeSubTypes[contentType].forEach((subType) => {
        newActiveSubTypes.delete(subType);
      });
    } else {
      newActiveContentTypes.add(contentType);
      contentTypeSubTypes[contentType].forEach((subType) => {
        newActiveSubTypes.add(subType);
      });
    }

    updateContentTypes(newActiveContentTypes);
    updateSubTypes(newActiveSubTypes);
  };

  const handleSubTypeToggle = (subType: SubType) => {
    const newActiveSubTypes = new Set(activeSubTypes);
    const newActiveContentTypes = new Set(activeContentTypes);

    const parentContentType = Object.keys(contentTypeSubTypes).find((type) =>
      contentTypeSubTypes[type as ContentType].includes(subType)
    ) as ContentType;

    if (activeSubTypes.has(subType)) {
      newActiveSubTypes.delete(subType);
      const remaining = contentTypeSubTypes[parentContentType].filter(
        (st) => st !== subType && newActiveSubTypes.has(st)
      );
      if (remaining.length === 0) {
        newActiveContentTypes.delete(parentContentType);
      }
    } else {
      newActiveSubTypes.add(subType);
      newActiveContentTypes.add(parentContentType);
    }

    updateContentTypes(newActiveContentTypes);
    updateSubTypes(newActiveSubTypes);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="tend-micro">Content Types</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {[ContentType.TEXT, ContentType.AUDIO, ContentType.VIDEO, ContentType.IMAGE].map(
          (contentType) => (
            <button
              key={contentType}
              type="button"
              className={`tend-chip${activeContentTypes.has(contentType) ? ' is-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleContentTypeToggle(contentType);
              }}
            >
              {TYPE_NAMES[contentType]}
            </button>
          )
        )}
      </div>

      <div className="tend-micro" style={{ marginTop: 6 }}>Subcontent Types</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {Object.values(SubType)
          .filter((subType) => SUBTYPE_NAMES[subType])
          .map((subType) => (
            <button
              key={subType}
              type="button"
              className={`tend-chip${activeSubTypes.has(subType) ? ' is-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSubTypeToggle(subType);
              }}
            >
              {SUBTYPE_NAMES[subType]}
            </button>
          ))}
      </div>
    </div>
  );
};

export default FilterBlock;
